import type { DocsSection } from "@/presentation/data/docs/docs-content.types";

export const architectureSection: DocsSection = {
  label: "Architecture",
  pages: [
    {
      slug: "architecture-overview",
      title: "Overview",
      description: "Hexagonal ports/adapters, with apps/api as the sole integrator.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            { code: "apps/api" },
            " is a NestJS 11 service built as a hexagon. Webapp-facing modules depend only on ",
            { strong: "ports" },
            "; adapters implement those ports; the concrete adapter is bound by config. ",
            "Adapters return domain DTOs (from ",
            { code: "packages/api-contract" },
            "), never raw ERPNext DocTypes or PIM payloads.",
          ],
        },
        {
          kind: "heading",
          id: "hexagon",
          text: "The hexagon",
        },
        {
          kind: "table",
          headers: ["Path", "Role"],
          rows: [
            [
              { code: "src/integrations/erp/" },
              [
                { code: "ErpPort" },
                " + adapters (erpnext/, noop/), DI token ",
                { code: "ERP_PROVIDER" },
              ],
            ],
            [
              { code: "src/integrations/pim/" },
              [
                { code: "PimPort" },
                " + adapters (unopim/, noop/), DI token ",
                { code: "PIM_PROVIDER" },
              ],
            ],
            [
              { code: "src/integrations/integrations.module.ts" },
              "Binds ports to adapters via integration.config.ts.",
            ],
            [{ code: "src/catalog/" }, "Webapp-facing module; depends only on the port tokens."],
            [
              { code: "src/health/" },
              "/api/health + /api/health/integrations (per-provider reachability).",
            ],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Adapters-only rule",
          text: "Product dialect lives only behind the port. The web app and use-cases depend on the port tokens, never a concrete adapter or a product URL.",
        },
      ],
    },
    {
      slug: "provider-model",
      title: "Provider model",
      description: "Config-driven ERP/PIM selection and the noop adapter.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "Each integration has a provider chosen by an environment variable bound to a DI token: ",
            { code: "ERP_PROVIDER" },
            " (",
            { code: "erpnext" },
            " | ",
            { code: "none" },
            ") and ",
            { code: "PIM_PROVIDER" },
            " (",
            { code: "unopim" },
            " | ",
            { code: "none" },
            ").",
          ],
        },
        {
          kind: "heading",
          id: "noop",
          text: "The noop (none) adapter",
        },
        {
          kind: "paragraph",
          text: [
            "Every provider keeps a ",
            { code: "noop" },
            " variant. Setting a provider to ",
            { code: "none" },
            " routes to it: the platform still boots and serves an (empty) catalog. The noop adapter returns empty/",
            { code: "disabled" },
            " envelopes and ",
            { strong: "never throws" },
            " — this is the disconnect/swap lever.",
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Cold / hot split",
          text: "ERPNext owns price + stock + orders (hot). UnoPIM owns the cold catalog (read-only, joined on the PIM uuid — the field is still named akeneo_id, kept as the persisted join key). Never sync price or stock from the PIM.",
        },
      ],
    },
    {
      slug: "data-flow",
      title: "Data flow",
      description: "Buyer app to apps/api to ERPNext/UnoPIM, and the response envelope.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: "A read flows in one direction. The web app never branches on a product; it only reads the envelope.",
        },
        {
          kind: "code",
          language: "text",
          code: "buyer-web ──REST /api/*──▶ apps/api ──port──▶ adapter ──▶ ERPNext / UnoPIM\n                                  │\n                                  └──▶ ApiResponse<T> { success, data, error }",
          caption: null,
        },
        {
          kind: "heading",
          id: "envelope",
          text: "The ApiResponse<T> envelope",
        },
        {
          kind: "paragraph",
          text: [
            "Every response — success, empty, or error — uses the same shape from ",
            { code: "packages/shared-types" },
            ". The web app holds no product secret and never learns a product URL.",
          ],
        },
        {
          kind: "code",
          language: "ts",
          code: "type ApiResponse<T> =\n  | { success: true; data: T; error: null }\n  | { success: false; data: null; error: { code: string; message: string } };",
          caption: "Illustrative shape — the canonical type lives in packages/shared-types.",
        },
      ],
    },
  ],
};
